import type { Address } from "@shared/types";
import { fromBase64, toBase58, toWalletStandardChain } from "@shared/utils/solWalletUtils";

import type { SolFunderRole, SolSendTransaction } from "@halliday-sdk/payments";

/**
 * Structural, dependency-free mirror of the pieces of the
 * [Solana Wallet Standard](https://docs.phantom.com/developer-powertools/wallet-standard) this
 * adapter needs. Declared locally on purpose so neither `@wallet-standard/base` nor
 * `@solana/web3.js` becomes a dependency of the SDK — the feature takes raw bytes, so nothing here
 * ever deserializes a `VersionedTransaction`.
 */
export interface SolWalletAccount {
    address: string;
}

export interface SolSignAndSendTransactionOutput {
    signature: Uint8Array;
}

export interface SolWalletStandardWallet {
    accounts: readonly SolWalletAccount[];
    features: {
        "solana:signAndSendTransaction": {
            signAndSendTransaction(input: {
                account: SolWalletAccount;
                transaction: Uint8Array;
                chain: string;
            }): Promise<readonly SolSignAndSendTransactionOutput[]>;
        };
    };
}

/**
 * Narrow an untrusted host object to the wallet-standard shape. The declared type above is satisfied
 * with NO cast by `WalletWithFeatures<SolanaSignAndSendTransactionFeature>` — the spec's own
 * narrowing helper — so a TS host that feature-checks first is already safe. This guard exists for
 * the hosts the type cannot reach: a JS caller, or one that cast a bare `Wallet` (whose
 * `features: IdentifierRecord<unknown>` does not assign). Both can hand over a LEGACY injected
 * provider instead (`window.solana` is `{ publicKey, connect, signMessage }` — no `accounts`, no
 * `features`), and without this the next line throws an opaque
 * `TypeError: Cannot read properties of undefined (reading '0')`.
 */
const asWalletStandard = (wallet: SolWalletStandardWallet): SolWalletStandardWallet => {
    if (!wallet || !Array.isArray(wallet.accounts) || !wallet.features) {
        throw new Error(
            "Expected a Solana wallet-standard wallet (an `accounts` array and a `features` record). " +
                "A legacy injected provider such as `window.solana` is not supported — resolve the wallet " +
                "from the wallet-standard registry (`getWallets()`) instead.",
        );
    }
    return wallet;
};

/**
 * Connect a Solana wallet as a funder — the SOL analog of `connectSigner` (`./ethers`) and
 * `connectWalletClient` (`./viem`), and like them it takes a *getter* for the live connection so the
 * host can reconnect without re-registering.
 *
 * The transaction is built server-side (`POST /payments/transfer-tx`) and arrives base64-encoded, so
 * this adapter only decodes it to bytes and hands it to the wallet's `solana:signAndSendTransaction`
 * feature. That feature broadcasts via the wallet's own RPC, so there is no `Connection`, no
 * blockhash handling and no confirmation wait here — payment-status polling detects the transfer.
 *
 * The wallet must come from the wallet-standard registry (`getWallets()` in `@wallet-standard/app`),
 * NOT from a legacy injected provider like `window.solana` — see `asWalletStandard`.
 *
 * @param {function} getWallet Function returning the live wallet-standard wallet.
 * @returns {SolFunderRole} ``walletType`` The literal `"SOL"` that discriminates the `FunderRole` union.
 * @returns {function} ``getAddress`` Function that returns the connected account's base58 address.
 * @returns {SolSendTransaction} ``sendTransaction`` Function that signs and sends a prepared transaction.
 */
export function connectSolWallet(getWallet: () => SolWalletStandardWallet): SolFunderRole {
    const getAddress = async (): Promise<Address> => {
        const account = asWalletStandard(getWallet()).accounts[0];
        if (!account) throw new Error("No Solana account connected");
        return account.address;
    };

    const sendTransaction: SolSendTransaction = async (transaction, chainConfig) => {
        const wallet = asWalletStandard(getWallet());

        // The fee payer is baked into the server-built transaction bytes, and the funder registry
        // already routed this call BY `transaction.from`. So a miss here means the wallet's authorized
        // accounts drifted since registration — signing with any other account can only produce a
        // transaction missing the required signature, which fails opaquely at the wallet or the
        // cluster. Fail loudly instead of substituting an account.
        const account = wallet.accounts.find((a) => a.address === transaction.from);
        if (!account) throw new Error(`Solana account ${transaction.from} is not connected`);

        const feature = wallet.features["solana:signAndSendTransaction"];
        if (!feature) throw new Error("Wallet does not support solana:signAndSendTransaction");

        const [output] = await feature.signAndSendTransaction({
            account,
            transaction: fromBase64(transaction.serializedTransaction),
            chain: toWalletStandardChain(chainConfig),
        });
        if (!output) throw new Error("Wallet returned no signature");

        return { signature: toBase58(output.signature) };
    };

    return { walletType: "SOL", getAddress, sendTransaction };
}
